import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import '../AiGovernance/AiGovernance.css';
import './isms.css';
import IsmsNav from './IsmsNav';
import { getOrgPermissions } from '../../utils/permissions';
import {
  getSoa, saveSoaControl, approveSoa, listSoaVersions, getProfile, saveProfile, listOrgUsers,
  downloadFile, userLabel, errMsg,
} from './ismsApi';
import axiosInstance from '../api';

const REASONS = ['RISK', 'LEGAL', 'CONTRACTUAL', 'BUSINESS', 'BEST_PRACTICE'];
const IMPL = ['IMPLEMENTED', 'PARTIAL', 'PLANNED', 'NOT_IMPLEMENTED'];
const statusBadge = (s) => (s === 'PASS' ? 'aig-b-pass' : s === 'AUTO_SATISFIED' ? 'aig-b-auto' : s === 'FAIL' ? 'aig-b-fail' : 'aig-b-na');
const FILTERS = [
  ['ALL', 'Isms.fAll', 'All'], ['APPLICABLE', 'Isms.fApplicable', 'Applicable'], ['EXCLUDED', 'Isms.fExcluded', 'Excluded'],
  ['SUGGESTED', 'Isms.fSuggested', 'With suggestion'], ['NOTEST', 'Isms.fNoTest', 'No test yet'],
];

export default function StatementOfApplicability() {
  const { t } = useTranslation();
  const { canManageFrameworks } = getOrgPermissions();
  const [soa, setSoa] = useState(null);
  const [profile, setProfile] = useState(null);
  const [versions, setVersions] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('ALL');
  const [q, setQ] = useState('');
  const [edit, setEdit] = useState(null);
  const [approve, setApprove] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p, v] = await Promise.all([getSoa(), getProfile().catch(() => null), listSoaVersions().catch(() => [])]);
      setSoa(s);
      setProfile(p);
      setVersions(v || []);
      setError('');
    } catch (e) {
      setSoa(null);
      setError(errMsg(e, t('Isms.soaUnavailable', 'No applicability data. Adopt ISO 27001 first.')));
    } finally {
      setLoading(false);
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { listOrgUsers().then(setUsers).catch(() => {}); }, []);

  const groups = useMemo(() => {
    if (!soa) return [];
    const needle = q.trim().toLowerCase();
    const rows = (soa.controls || []).filter((c) => {
      if (needle && !`${c.controlRef} ${c.name}`.toLowerCase().includes(needle)) return false;
      if (filter === 'APPLICABLE') return c.applicable;
      if (filter === 'EXCLUDED') return !c.applicable;
      if (filter === 'SUGGESTED') return !!c.suggestion;
      if (filter === 'NOTEST') return !c.hasTest;
      return true;
    });
    const map = new Map();
    rows.forEach((c) => {
      const k = c.category || 'Other';
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(c);
    });
    return Array.from(map.entries());
  }, [soa, filter, q]);

  const openEdit = (c) => setEdit({
    controlId: c.controlId, ref: c.controlRef, name: c.name, applicable: c.applicable, justification: c.justification || '',
    inclusionReason: c.inclusionReason || '', implementationStatus: c.implementationStatus || '', ownerUserId: c.ownerUserId || '',
  });

  const submit = async (controlId, body) => {
    setBusy(true);
    try {
      await saveSoaControl(controlId, body);
      setEdit(null);
      setError('');
      await load();
    } catch (e) {
      setError(errMsg(e, t('Isms.saveFailed', 'Could not save.')));
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = () => {
    if (!edit.applicable && !edit.justification.trim()) {
      setError(t('Isms.justificationRequired', 'A justification is required to exclude a control.'));
      return;
    }
    submit(edit.controlId, {
      applicable: edit.applicable,
      justification: edit.applicable ? null : edit.justification.trim(),
      inclusionReason: edit.inclusionReason || null,
      implementationStatus: edit.implementationStatus || null,
      ownerUserId: edit.ownerUserId ? Number(edit.ownerUserId) : null,
    });
  };

  // Accepting a suggestion submits it as a normal decision, keeping any decision data already on the row.
  const accept = (c) => submit(c.controlId, {
    applicable: c.suggestion.applicable,
    justification: c.suggestion.applicable ? null : c.suggestion.reason,
    inclusionReason: c.inclusionReason || null,
    implementationStatus: c.implementationStatus || null,
    ownerUserId: c.ownerUserId || null,
  });

  const updateProfile = async (key, value) => {
    try {
      const next = await saveProfile({ ...(profile || {}), [key]: value });
      setProfile(next);
      await load();
    } catch (e) { setError(errMsg(e, t('Isms.saveFailed', 'Could not save.'))); }
  };

  const doApprove = async () => {
    setBusy(true);
    try {
      await approveSoa(approve || '');
      setApprove(null);
      setMsg(t('Isms.soaApproved', 'Statement of Applicability approved and a version snapshot was saved.'));
      await load();
    } catch (e) { setError(errMsg(e, t('Isms.saveFailed', 'Could not save.'))); } finally { setBusy(false); }
  };

  const openHtml = async () => {
    try {
      const r = await axiosInstance.get('/isms/soa/export/html', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'text/html' }));
      window.open(url, '_blank', 'noopener');
    } catch (e) { setError(errMsg(e, t('Isms.downloadFailed', 'Download failed.'))); }
  };
  const csv = () => downloadFile('/isms/soa/export', 'statement-of-applicability.csv', 'text/csv')
    .catch((e) => setError(errMsg(e, t('Isms.downloadFailed', 'Download failed.'))));

  const latest = soa?.latestVersion;
  const boolCtl = (key, label) => (
    <label className="isms-check">
      <select
        value={profile && profile[key] != null ? String(profile[key]) : ''}
        disabled={!canManageFrameworks}
        onChange={(e) => updateProfile(key, e.target.value === '' ? null : e.target.value === 'true')}
      >
        <option value="">{t('Isms.unanswered', 'Not answered')}</option>
        <option value="true">{t('Isms.yes', 'Yes')}</option>
        <option value="false">{t('Isms.no', 'No')}</option>
      </select>
      {label}
    </label>
  );

  return (
    <div className="aig-page">
      <div className="aig-header">
        <h1 className="aig-title">{t('Isms.soaTitle', 'Statement of Applicability')}</h1>
        <p className="aig-subtitle">{t('Isms.soaSubtitle', 'All Annex A controls with your decision, the reason, and the implementation status. Auditors read every justification.')}</p>
      </div>

      {soa && (
        <div className="aig-stat-bar">
          <div className="aig-stat"><div className="aig-stat-label">{t('Isms.statControls', 'Controls')}</div><div className="aig-stat-val">{soa.totalControls}</div></div>
          <div className="aig-stat"><div className="aig-stat-label">{t('Isms.statApplicableShort', 'Applicable')}</div><div className="aig-stat-val">{soa.applicableCount}</div></div>
          <div className="aig-stat"><div className="aig-stat-label">{t('Isms.statExcluded', 'Excluded')}</div><div className="aig-stat-val">{soa.excludedCount}</div></div>
          <div className="aig-stat"><div className="aig-stat-label">{t('Isms.statImplemented', 'Implemented')}</div><div className="aig-stat-val">{soa.implementedCount}</div></div>
          <div className="aig-stat"><div className="aig-stat-label">{t('Isms.statReadiness', 'Readiness')}</div>
            <div className={`aig-stat-val ${soa.readinessPct >= 80 ? 'aig-val-ok' : soa.readinessPct >= 50 ? 'aig-val-warn' : 'aig-val-bad'}`}>{soa.readinessPct}%</div></div>
          <div className="aig-stat"><div className="aig-stat-label">{t('Isms.statApprovedV', 'Approved version')}</div><div className="aig-stat-val">{latest ? `v${latest.version}` : '-'}</div></div>
        </div>
      )}
      <IsmsNav />

      {error && <div className="isms-error">{error}</div>}
      {msg && <div className="isms-ok">{msg}</div>}

      {profile && (
        <div className="isms-panel">
          <h4>{t('Isms.profileTitle', 'About your company (drives the suggestions)')}</h4>
          <div className="isms-row">
            {boolCtl('remoteOnly', t('Isms.qRemote', 'We are fully remote with no office'))}
            {boolCtl('inHouseDev', t('Isms.qDev', 'We develop software in-house'))}
            {boolCtl('onPremInfra', t('Isms.qOnPrem', 'We run on-premises infrastructure'))}
          </div>
          <div className="aig-muted" style={{ fontSize: '0.75rem', marginTop: '0.4rem' }}>
            {t('Isms.profileHelp', 'Suggestions are proposals only. Nothing is excluded until you accept it.')}
          </div>
        </div>
      )}

      <div className="aig-toolbar">
        <input className="aig-btn" style={{ minWidth: 220 }} placeholder={t('Isms.search', 'Search controls...')} value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="aig-btn" value={filter} onChange={(e) => setFilter(e.target.value)}>
          {FILTERS.map(([k, i18n, label]) => <option key={k} value={k}>{t(i18n, label)}</option>)}
        </select>
        <div className="aig-spacer" />
        <button type="button" className="aig-btn" onClick={csv}>{t('Isms.export', 'Export CSV')}</button>
        <button type="button" className="aig-btn" onClick={openHtml}>{t('Isms.printable', 'Printable view')}</button>
        {canManageFrameworks && (
          <button type="button" className="aig-btn aig-btn-primary" disabled={busy || !soa} onClick={() => setApprove('')}>{t('Isms.approveVersion', 'Approve version')}</button>
        )}
      </div>

      {loading ? <div className="aig-spinner" /> : !soa ? (
        <div className="aig-empty">{t('Isms.soaUnavailable', 'No applicability data. Adopt ISO 27001 first.')}</div>
      ) : groups.length === 0 ? (
        <div className="aig-empty">{t('Isms.noMatch', 'No controls match.')}</div>
      ) : (
        <table className="aig-table">
          <thead><tr>
            <th>{t('Isms.colControl', 'Control')}</th><th>{t('Isms.colName', 'Name')}</th><th>{t('Isms.colDecision', 'Decision')}</th>
            <th>{t('Isms.colImpl', 'Implementation')}</th><th>{t('Isms.colAudit', 'Audit')}</th><th>{t('Isms.colRisks', 'Risks')}</th><th />
          </tr></thead>
          <tbody>
            {groups.map(([cat, rows]) => (
              <React.Fragment key={cat}>
                <tr><td colSpan={7} className="isms-group-head">{cat} ({rows.length})</td></tr>
                {rows.map((c) => (
                  <tr key={c.controlId}>
                    <td><strong>{c.controlRef}</strong></td>
                    <td>
                      {c.name}
                      {!c.applicable && c.justification && <div className="aig-muted">{c.justification}</div>}
                      {c.suggestion && (
                        <div style={{ marginTop: '0.25rem' }}>
                          <span className="aig-muted" style={{ fontSize: '0.75rem' }}>
                            {c.suggestion.applicable ? t('Isms.suggestInclude', 'Suggested: applicable') : t('Isms.suggestExclude', 'Suggested: not applicable')} - {c.suggestion.reason}
                          </span>{' '}
                          {canManageFrameworks && <button type="button" className="isms-chip" disabled={busy} onClick={() => accept(c)}>{t('Isms.accept', 'Accept')}</button>}
                        </div>
                      )}
                    </td>
                    <td><span className={`aig-badge ${c.applicable ? 'aig-b-pass' : 'aig-b-na'}`}>{c.applicable ? t('Isms.applicable', 'Applicable') : t('Isms.notApplicable', 'Excluded')}</span>
                      {c.inclusionReason && <div className="aig-muted">{c.inclusionReason}</div>}</td>
                    <td>{c.implementationStatus || <span className="aig-muted">-</span>}</td>
                    <td>{c.auditStatus ? <span className={`aig-badge ${statusBadge(c.auditStatus)}`}>{c.auditStatus}</span> : <span className="aig-muted">{c.hasTest ? '-' : t('Isms.noTest', 'no test')}</span>}</td>
                    <td>{(c.linkedRiskIds || []).length || <span className="aig-muted">-</span>}</td>
                    <td>{canManageFrameworks && <button type="button" className="isms-link" onClick={() => openEdit(c)}>{t('Isms.edit', 'Edit')}</button>}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {versions.length > 0 && (
        <div className="isms-panel" style={{ marginTop: '1rem' }}>
          <h4>{t('Isms.versions', 'Approved versions')}</h4>
          <table className="aig-table">
            <tbody>
              {versions.map((v) => (
                <tr key={v.id}><td>v{v.version}</td><td>{v.approvedBy}</td><td>{v.approvedAt ? String(v.approvedAt).slice(0, 10) : ''}</td><td>{v.summary}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {edit && (
        <div className="aig-overlay" onClick={() => setEdit(null)}>
          <div className="aig-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{edit.ref} {edit.name}</h3>
            <div className="aig-field">
              <label>{t('Isms.colDecision', 'Decision')}</label>
              <select value={edit.applicable ? 'yes' : 'no'} onChange={(e) => setEdit({ ...edit, applicable: e.target.value === 'yes' })}>
                <option value="yes">{t('Isms.applicable', 'Applicable')}</option>
                <option value="no">{t('Isms.notApplicable', 'Excluded (not applicable)')}</option>
              </select>
            </div>
            {!edit.applicable ? (
              <div className="aig-field">
                <label>{t('Isms.justification', 'Justification for exclusion (required)')}</label>
                <textarea rows={3} value={edit.justification} onChange={(e) => setEdit({ ...edit, justification: e.target.value })} />
              </div>
            ) : (
              <div className="aig-field">
                <label>{t('Isms.inclusionReason', 'Why is it included?')}</label>
                <select value={edit.inclusionReason} onChange={(e) => setEdit({ ...edit, inclusionReason: e.target.value })}>
                  <option value="">-</option>{REASONS.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
            )}
            <div className="aig-field">
              <label>{t('Isms.colImpl', 'Implementation')}</label>
              <select value={edit.implementationStatus} onChange={(e) => setEdit({ ...edit, implementationStatus: e.target.value })}>
                <option value="">-</option>{IMPL.map((x) => <option key={x} value={x}>{x}</option>)}
              </select>
            </div>
            <div className="aig-field">
              <label>{t('Isms.controlOwner', 'Control owner')}</label>
              <select value={edit.ownerUserId} onChange={(e) => setEdit({ ...edit, ownerUserId: e.target.value })}>
                <option value="">-</option>{users.map((u) => <option key={u.id} value={u.id}>{userLabel(u)}</option>)}
              </select>
            </div>
            <div className="aig-modal-actions">
              <button type="button" className="aig-btn" onClick={() => setEdit(null)}>{t('common.cancel', 'Cancel')}</button>
              <button type="button" className="aig-btn aig-btn-primary" disabled={busy} onClick={saveEdit}>{t('common.save', 'Save')}</button>
            </div>
          </div>
        </div>
      )}

      {approve !== null && (
        <div className="aig-overlay" onClick={() => setApprove(null)}>
          <div className="aig-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{t('Isms.approveVersion', 'Approve version')}</h3>
            <p className="aig-muted" style={{ fontSize: '0.8125rem' }}>{t('Isms.approveSoaHelp', 'This freezes the current decisions as a dated version. Describe what changed and who approved it for management.')}</p>
            <div className="aig-field"><label>{t('Isms.summary', 'Summary')}</label><textarea rows={3} value={approve} onChange={(e) => setApprove(e.target.value)} /></div>
            <div className="aig-modal-actions">
              <button type="button" className="aig-btn" onClick={() => setApprove(null)}>{t('common.cancel', 'Cancel')}</button>
              <button type="button" className="aig-btn aig-btn-primary" disabled={busy} onClick={doApprove}>{t('Isms.approve', 'Approve')}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
